import React, { useState } from 'react';
import ReactLoading from 'react-loading';
import { Button } from 'components/common/Button';
import { FormErrorMessage, FormField, FormLabelWrapper } from 'components/common/form-utils/form-elements';
import { FormState, useForm } from 'components/common/form-utils/form-state';
import { validateForm, validateFormField, Validators } from 'components/common/form-utils/validators';
import { Voucher } from 'utils/Voucher';
import Select from 'react-select';
import { requests } from 'api-services';

type VoucherUnderConstruction = Partial<Voucher>

const VoucherCreatorPage = () => {
  const [state, setState] = useState<'ready' | 'creating' | 'error' | 'done'>('ready');

  const form: FormState = useForm({
    code: { visibleName: 'Code', required: true },
    discountType: { visibleName: 'Type', required: true },
    percentageDiscountValue: { visibleName: 'Value', validations: [Validators.isInteger(), Validators.minValue(1), Validators.maxValue(100)] },
    fixedDiscountValue: { visibleName: 'Value', validations: [Validators.isInteger(), Validators.minValue(0.01)] },
    isValid: { visibleName: 'Valid', type: 'boolean' },
  });

  form.percentageDiscountValue.requiredIf = () => {
    return (form.discountType.value === 'Percentage');
  };

  form.fixedDiscountValue.requiredIf = () => {
    return (form.discountType.value === 'Fixed');
  };

  const getVoucher = (): VoucherUnderConstruction => {
    const voucher: Voucher = {
      code: '',
      discountType: 'Percentage',
      discountValue: 0,
      isValid: false,
    };

    if (form.code.value && !form.code.error) voucher.code = form.code.value;
    if (form.discountType.value && !form.discountType.error) {
      if (form.discountType.value === 'Percentage' || form.discountType.value === 'Fixed') {
        voucher.discountType = form.discountType.value;
      }
    }
    if (form.percentageDiscountValue.value && !form.percentageDiscountValue.error) {
      voucher.discountValue = Number.parseInt(form.percentageDiscountValue.value);
    }
    if (form.fixedDiscountValue.value && !form.fixedDiscountValue.error) {
      voucher.discountValue = Number.parseInt(form.fixedDiscountValue.value);
    }
    if (form.isValid.value && !form.isValid.error) voucher.isValid = form.isValid.getBoolean();
    return voucher;
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm(form)) return;

    setState('creating');
    const result = getVoucher() as Voucher;
    requests.voucher.post(result)
      .then(() => {
        setState('done');
      })
      .catch(() => setState('error'));
  };

  return <div className='my-8'>
    <div className='text-2xl pb-4 font-medium'>Create Voucher</div>
    <div className='flex justify-center flex-row py-8'>
      {state === 'creating' && <ReactLoading className='m-auto' type={'bubbles'} color={'#ac542e'} height={'50%'} width={'8%'} />}
      {state === 'error' && <span>An error occurred creating the Voucher.</span>}
      {state === 'done' && <div className='flex flex-col mt-4'><span className=''>Voucher {form.code.value} created.</span>
        <Button primary id='back_button' className='mt-10' linkTo='/admin/Vouchers'>Back to Vouchers</Button>
      </div>}
      {state === 'ready'
        && <form onSubmit={onSubmit}>
          <FormField hideRequiredStar validateOnChange field={form.code} />

          <FormLabelWrapper
            name='Type'
            id='voucher-type-label'
            input={<React.Fragment>
              <div className='w-full flex flex-row'>
                <Select
                  id='discountType'
                  aria-labelledby='voucher-type-label'
                  className={`w-48 border border-2 ${form.discountType.error ? 'border-red-200' : 'border-gray-300'}`}
                  styles={{
                    control: (provided) => ({ ...provided, 'border': 'none' }),
                  }}
                  options={[{ value: 'Percentage', label: 'Percentage' }, { value: 'Fixed', label: 'Fixed' }]}
                  onChange={event => {
                    event && validateFormField(form.discountType, event.value);
                    event && form.discountType.setValue(event.value);
                  }}
                />
                <span className='pl-4 flex items-center'>
                  {form.discountType.error && <FormErrorMessage>{form.discountType.error}</FormErrorMessage>}
                </span>
              </div>
            </React.Fragment>}
          />
          {form.discountType.value === 'Percentage' && <FormField hideRequiredStar validateOnChange field={form.percentageDiscountValue} />}
          {form.discountType.value === 'Fixed' && <FormField hideRequiredStar validateOnChange field={form.fixedDiscountValue} />}
          <FormLabelWrapper name='Valid' htmlFor='valid' input={
            <div className='w-full'>
              <input
                type="checkbox"
                id='valid'
                onChange={e => {
                  form.isValid.setBoolean(e.target.checked);
                }}
                className="form-checkbox mt-2" />
            </div>
          } />
          <div className='flex flex-row w-full place-content-end space-x-1 pr-4'>
            <Button grey id='cancel-button' linkTo='/admin/Vouchers'>Cancel</Button>
            <Button type='submit' primary id='create-button' className='w-32'>Create Voucher</Button>
          </div>
        </form>}
    </div>
  </div>;
};
export default VoucherCreatorPage;
